import { ApiError, problemTypeOf, type Workspace } from "@baton/api";
import { itemPresentationKey, lastRouteKey, lastWorkspaceKey, peekWidthKey, sidebarCollapsedKey, sidebarWidthKey } from "@baton/core";
import { messageKey, useI18n } from "@baton/i18n";
import { ConnectionBanner, detectPlatform, ErrorState, frameGroundClassName, frameSheetClassName, OfflineBanner, PEEK_DEFAULT_WIDTH, RecentColorsProvider, RecordReferenceProvider, toast, useScrollFade, useTheme } from "@baton/ui";
import { useQuery } from "@tanstack/react-query";
import { createContext, lazy, Suspense, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate, useParams, useSearchParams } from "react-router";
import { problemOf } from "../../app/problems.ts";
import { formerAddressQuery } from "../../app/queries.ts";
import { useRuntime } from "../../app/RuntimeProvider.tsx";
import { useUiStore } from "../../app/uiStore.ts";
import { VerifyEmailBanner } from "../../auth/verify/VerifyEmailBanner.tsx";
import { isUnverified } from "../../auth/verify/useEmailVerification.ts";
import { NoAccess } from "../../errors/NoAccess.tsx";
import { TeamMoved } from "../../errors/TeamMoved.tsx";
import { TeamSuspended } from "../../errors/TeamSuspended.tsx";
import { SheetLogo } from "../../routes/SheetLogo.tsx";
import { chunks } from "../../routes.tsx";
import { AppShell, SIDEBAR_DEFAULT } from "./AppShell.tsx";
import { useConnectionState, useOfflineState, useQueuedWrites } from "../state/connection.ts";
import { featuresOf, FeaturesProvider } from "../state/features.ts";
import { useTeamRecentColours } from "../state/recentColours.ts";
import { useUnreadTotal } from "../topbar/InboxBadge.tsx";
import { GLOBAL_SHORTCUT_ROWS, useKeyboardMap, type GlobalShortcutId, type Shortcut } from "../keyboard/keyboard.ts";
import { availableModes, HOME_MODE, isNestedPage, MODES, modeOfPath, modeRouteFor, rememberModeRoute, type ModeSpec } from "../modes/modes.tsx";
import { accessOf } from "../state/access.ts";
import { SwitcherPlaceholder } from "../sidebar/placeholders.tsx";
import { NoAccessSkeleton } from "../../skeletons/Skeletons.tsx";
import { DialogLoading, LogInteractionLoading } from "./DialogLoading.tsx";
import { prefetchOnIdle } from "../../app/prefetch.ts";
import { canCreateProject, isWorkspaceAdmin, isWorkspaceMember } from "../state/permissions.ts";
import { PEEK_PARAM, PeekSlot } from "../peek/PeekSlot.tsx";
import { RestoreNotice } from "../realtime/RestoreNotice.tsx";
import { useWorkspaceStream } from "../realtime/useWorkspaceStream.ts";
import { HelpMenu } from "../rail/HelpMenu.tsx";
import { Rail } from "../rail/Rail.tsx";
import { RailAccount } from "../rail/RailAccount.tsx";
import { useShellData } from "../state/shellData.ts";
import { useFollowTeamAddress } from "../state/teamAddress.ts";
import { Sidebar } from "../sidebar/Sidebar.tsx";
import { nextTheme } from "../state/themeControl.ts";
import { QuickCreateButton, useQuickCreate } from "../topbar/quickCreate.tsx";
import { parentOf, TopBar, TopBarProvider, useTopBarStore, type BackTarget } from "../topbar/TopBar.tsx";
import { useOrigin } from "../history/origin.ts";
import { WorkspaceSwitcher } from "../sidebar/WorkspaceSwitcher.tsx";
import { useCloseReasonStore } from "../../item/closure/closeReasonStore.ts";
import { useTicketsUnread } from "../state/ticketsUnread.ts";
import { useRemindStore } from "../../item/remind/remindStore.ts";
import { AssistantTile } from "../../assistant/AssistantTile.tsx";
import { DockFallback } from "../../assistant/DockFallback.tsx";
import { assistantSettingsQuery } from "../../assistant/settingsQuery.ts";
import { PANEL_MIN_WIDTH, useAssistantPanel } from "../../assistant/store.ts";
import { EMAIL_PARAM } from "../../m365/emails/params.ts";
import { PEEK_MIN_WIDTH } from "@baton/ui";

/**
 * The layout route of `/{ws}` (`contracts/screens.md` § The shell): mounts once per
 * workspace, loads the shell data, resolves the feature availability, records the last
 * workspace, its last route and the current mode's last route (FR-015-005), derives the rail
 * mode from the URL so the sidebar shows that mode's tree on a deep link or a reload, installs
 * the global keyboard map, and renders the rail, the sidebar, the top bar, the side peek slot, the palette, the dialogs and the
 * route's screen on the work surface. It holds the workspace's event stream (FR-015-003), which feeds
 * the connection indicator, and the restore notice (FR-015-155), and hands every rendered body below it the record
 * reference that places a link to a record in this client's route table (FR-015-150 § URL schemes as amended
 * 2026-10-15, `platform/references/RecordReference.tsx`). A 404 or 403 on the workspace renders the no-access
 * panel in place, or, when the address was one of the person's own Teams before it changed, the page that says where the
 * Team went (001 FR-157); a change of the address while the shell is open takes the person to the same screen at the new
 * one. The lazy route chunks are prefetched on idle (`research.md` W-26), so the
 * first click into a project does not wait for its module.
 */
export interface WorkspaceContextValue {
  readonly ws: string;
  readonly workspace: Workspace | undefined;
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

export function useWorkspaceContext(): WorkspaceContextValue {
  const value = useContext(WorkspaceContext);
  if (value === null) throw new Error("useWorkspaceContext must be used inside the workspace shell");
  return value;
}

/** The workspace context where the caller may render outside the shell (onboarding, the error screens), or `null` there. */
export function useOptionalWorkspaceContext(): WorkspaceContextValue | null {
  return useContext(WorkspaceContext);
}

const NewItemDialog = lazy(() => chunks.item().then((module) => ({ default: module.NewItemDialog })));
const CommandPalette = lazy(() => chunks.palette().then((module) => ({ default: module.CommandPalette })));
const NewProjectDialog = lazy(() => chunks.newProject().then((module) => ({ default: module.NewProjectDialog })));
const LogInteractionDialog = lazy(() => chunks.accountDialogs().then((module) => ({ default: module.LogInteractionDialog })));
const NewClientDialog = lazy(() => chunks.accountDialogs().then((module) => ({ default: module.NewClientDialog })));
const MoveDialog = lazy(() => chunks.moveDialog().then((module) => ({ default: module.MoveDialog })));
const CloseReasonDialog = lazy(() => chunks.closeReasonDialog().then((module) => ({ default: module.CloseReasonDialog })));
const RemindDialog = lazy(() => chunks.remindDialog().then((module) => ({ default: module.RemindDialog })));
const ShellShortcutReference = lazy(() => chunks.shortcutReference().then((module) => ({ default: module.ShellShortcutReference })));
// A record link draws its pill without the address while this loads (`RecordLinkPill` in @baton/ui).
const RecordReference = lazy(() => chunks.recordReference().then((module) => ({ default: module.RecordReference })));
const AssistantDock = lazy(() => chunks.assistant().then((module) => ({ default: module.AssistantDock })));

/** The width the page keeps between the sidebar and the docks before the docks narrow and the sidebar folds (`contracts/design-system.md` § Assistant panel). */
const PAGE_MIN_WIDTH = 440;
const RAIL_WIDTH = 72;
const SHEET_INSET = 16;

/**
 * How the two docks share a page that cannot hold them (§ Assistant panel): first each dock narrows
 * to its least width, then the sidebar folds to its peek mode for as long as both stay open.
 */
export function dockFold(viewport: number, sidebar: number, peekWidth: number | null, panelWidth: number | null): { readonly narrow: boolean; readonly foldSidebar: boolean } {
  if (peekWidth === null || panelWidth === null) return { narrow: false, foldSidebar: false };
  const room = (sidebarWidth: number, peek: number, panel: number) => viewport - RAIL_WIDTH - sidebarWidth - SHEET_INSET - peek - panel;
  if (room(sidebar, peekWidth, panelWidth) >= PAGE_MIN_WIDTH) return { narrow: false, foldSidebar: false };
  if (room(sidebar, PEEK_MIN_WIDTH, PANEL_MIN_WIDTH) >= PAGE_MIN_WIDTH) return { narrow: true, foldSidebar: false };
  return { narrow: true, foldSidebar: true };
}

function useViewportWidth(): number {
  const [width, setWidth] = useState(() => window.innerWidth);
  useEffect(() => {
    const onResize = () => {
      setWidth(window.innerWidth);
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, []);
  return width;
}

/** The open item when the route is the full page, the Team's or a client's (2026-10-07), or a peek is open. */
export function itemOfLocation(ws: string, pathname: string, search: URLSearchParams): string | null {
  const peek = search.get(PEEK_PARAM);
  if (peek !== null) return peek;
  const match = new RegExp(`^/${ws}/(?:clients/[^/]+/)?tasks/([^/]+)`).exec(pathname);
  return match?.[1] === undefined ? null : decodeURIComponent(match[1]);
}

export function WorkspaceShell() {
  const { t } = useI18n();
  const runtime = useRuntime();
  const navigate = useNavigate();
  const location = useLocation();
  const [search, setSearch] = useSearchParams();
  const theme = useTheme();
  const { ws = "", project = null } = useParams();
  const data = useShellData(runtime, ws);
  // The sheet's main region while the Team cannot be opened, which fades as the shell's does (`AppShell`, 2026-10-21).
  const main = useScrollFade<HTMLElement>();
  const features = useMemo(() => featuresOf(data.workspace.data), [data.workspace.data]);
  // 024 FR-024-026 to FR-024-028 (T037): the workspace read's palette goes to the provider once it resolves, so the
  // static accent stands until then and nothing flashes; a `theme.*` event re-reads the workspace, which repaints here.
  // Leaving the workspace, or losing access to it, returns the document to the static tokens.
  const palette = data.workspace.data?.theme ?? null;
  const { setPalette } = theme;
  useEffect(() => {
    setPalette(palette);
    return () => {
      setPalette(null);
    };
  }, [palette, setPalette]);
  const stream = useWorkspaceStream(runtime, ws, !data.noAccess, data.me.data?.id ?? null, data.workspace.data?.id ?? null);
  // 001 FR-159: a Team an instance admin suspended answers 403 `workspace_suspended` to its members, which is its own page and not a moved address.
  const suspended = data.noAccess && data.workspace.error instanceof ApiError && problemTypeOf(data.workspace.error.problem.type) === "workspace_suspended";
  const former = useQuery({ ...formerAddressQuery(runtime, ws), enabled: data.noAccess && !suspended });
  const teamNameForMove = data.workspace.data?.name ?? ws;
  const announceMove = useCallback(
    (to: string) => {
      toast({ variant: "info", title: t("teamMoved.followed", { name: teamNameForMove, address: `${window.location.host}/${to}` }) });
    },
    [t, teamNameForMove],
  );
  useFollowTeamAddress(runtime, ws, data.workspace.data?.id ?? null, announceMove);
  const connection = useConnectionState(runtime, stream);
  const offline = useOfflineState(runtime) === "offline";
  const queued = useQueuedWrites(runtime);
  const unread = useUnreadTotal(runtime, ws);
  const topBar = useTopBarStore();
  const ui = useUiStore();
  // The two task dialogs load on their first opening, as the other shell dialogs do (FR-015-120).
  const closing = useCloseReasonStore((state) => state.question);
  const settleClosing = useCloseReasonStore((state) => state.settle);
  const reminding = useRemindStore((state) => state.target);
  const closeRemind = useRemindStore((state) => state.close);
  const [sidebarWidth, setSidebarWidth] = useState(() => runtime.storage.get(sidebarWidthKey) ?? SIDEBAR_DEFAULT);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => runtime.storage.get(sidebarCollapsedKey) ?? false);
  // 019's panel (2026-10-14): its stored state is read before the first paint, so a reload opens it as it was; the tile and the dock are drawn only where the Team's settings let the panel open.
  const assistantPanel = useAssistantPanel();
  useState(() => {
    assistantPanel.hydrate(runtime.storage);
    return null;
  });
  const assistantSettings = useQuery({ ...assistantSettingsQuery(runtime, ws), enabled: !data.noAccess, retry: false });
  const assistantAvailable = assistantSettings.isSuccess && assistantSettings.data.is_enabled && assistantSettings.data.surfaces.web_text;
  const assistantOpen = assistantAvailable && assistantPanel.open;
  // The panel closes on a switch of Team (015 `contracts/screens.md` § 019 Assistant); the next Team's thread is its own.
  const lastWs = useRef(ws);
  useEffect(() => {
    if (lastWs.current === ws) return;
    lastWs.current = ws;
    if (assistantPanel.open) assistantPanel.setOpen(false);
  }, [ws, assistantPanel]);
  const toggleAssistant = useCallback(() => {
    if (!assistantAvailable) return;
    // Tapped in the phone's drawer, the tile closes the drawer so the sheet it opens is not under it.
    ui.setSidebarDrawerOpen(false);
    assistantPanel.toggle();
  }, [assistantAvailable, assistantPanel, ui]);
  const viewport = useViewportWidth();
  const peekOpen = search.has(PEEK_PARAM) || search.has(EMAIL_PARAM);
  const fold = dockFold(viewport, sidebarCollapsed ? 0 : sidebarWidth, peekOpen ? (runtime.storage.get(peekWidthKey) ?? PEEK_DEFAULT_WIDTH) : null, assistantOpen ? assistantPanel.width : null);
  const onSidebarCollapsedChange = useCallback(
    (collapsed: boolean) => {
      setSidebarCollapsed(collapsed);
      runtime.storage.set(sidebarCollapsedKey, collapsed);
    },
    [runtime],
  );
  const mac = useMemo(() => detectPlatform(navigator.userAgent) === "mac", []);
  const [retrying, setRetrying] = useState(false);
  const workspaceMember = isWorkspaceMember(data.me.data, ws);
  const recentColours = useTeamRecentColours(runtime, ws, project, workspaceMember);
  const grants = accessOf(data.me.data, ws);
  const available = useMemo(() => availableModes(features, { workspaceMember, grants }), [features, workspaceMember, grants]);
  // 028 FR-028-053: the Tickets tile counts the reader's unread messages over the Team's portals.
  const ticketsUnread = useTicketsUnread(ws, available.some((mode) => mode.id === "tickets"));
  const modes = useMemo(() => available.map((mode) => (mode.id === "tickets" ? { ...mode, unread: ticketsUnread } : mode)), [available, ticketsUnread]);
  const activeMode = modeOfPath(modes, ws, location.pathname);
  // A route no mode owns, which is the not-found screen, keeps the Home tree with no tile raised.
  const ModeSidebar = (activeMode ?? HOME_MODE).Sidebar;

  useEffect(() => {
    if (!data.workspace.isSuccess) return;
    runtime.storage.set(lastWorkspaceKey, ws);
    runtime.storage.set(lastRouteKey, `${location.pathname}${location.search}`, ws);
    if (activeMode !== null) rememberModeRoute(runtime, ws, activeMode, `${location.pathname}${location.search}`);
  }, [runtime, ws, location.pathname, location.search, data.workspace.isSuccess, activeMode]);

  const selectMode = useCallback(
    (mode: ModeSpec) => {
      void navigate(modeRouteFor(runtime, ws, mode));
    },
    [runtime, ws, navigate],
  );

  useEffect(() => {
    // The palette, the collection, the detail and the frame it opens in are the chunks a first keystroke or click reaches for; the record reference is the one every body with a record link draws.
    const cancels = [prefetchOnIdle(chunks.palette), prefetchOnIdle(chunks.collection), prefetchOnIdle(chunks.item), prefetchOnIdle(chunks.peekFrame), prefetchOnIdle(chunks.shortcutReference), prefetchOnIdle(chunks.recordReference), prefetchOnIdle(chunks.assistant)];
    return () => {
      for (const cancel of cancels) cancel();
    };
  }, []);

  const onSidebarWidthChange = useCallback(
    (width: number) => {
      setSidebarWidth(width);
      runtime.storage.set(sidebarWidthKey, width);
    },
    [runtime],
  );

  const togglePeek = useCallback(() => {
    const params = new URLSearchParams(search);
    if (params.has(PEEK_PARAM)) {
      params.delete(PEEK_PARAM);
      params.delete("present");
      params.delete("comment");
      setSearch(params, { replace: true });
      return;
    }
    const last = ui.lastPeek;
    if (last === null) return;
    params.set(PEEK_PARAM, last);
    // The shortcut toggles a frame over the screen, so with the full page remembered it names the side peek (015 FR-015-040 as amended 2026-10-05).
    if (runtime.storage.get(itemPresentationKey) === "page") params.set("present", "peek");
    setSearch(params, { replace: true });
  }, [search, setSearch, ui.lastPeek, runtime]);

  const quickCreate = useQuickCreate(ws);
  const shortcuts = useMemo<Shortcut[]>(() => {
    const runs: Readonly<Record<GlobalShortcutId, () => void>> = {
      palette: () => { ui.setPaletteOpen(true); },
      reference: () => { ui.setShortcutsOpen(true); },
      "quick-create": quickCreate.open,
      "go-inbox": () => { void navigate(`/${ws}/inbox`); },
      "go-my-work": () => { void navigate(`/${ws}/my-work`); },
      "go-search": () => { void navigate(`/${ws}/search`); },
      "go-projects": () => { void navigate(`/${ws}/projects`); },
      sidebar: () => { ui.toggleSidebarDrawer(); },
      peek: togglePeek,
      theme: () => { theme.setTheme(nextTheme(theme.theme)); },
      assistant: toggleAssistant,
    };
    return [...GLOBAL_SHORTCUT_ROWS.map((row) => ({ ...row, run: runs[row.id] })), { id: "reference-plain", keys: "?", label: messageKey("shortcuts.reference"), run: runs.reference }];
  }, [ui, navigate, ws, theme, togglePeek, quickCreate.open, toggleAssistant]);
  useKeyboardMap({ shortcuts, mac });

  // The Team's crumb reads the Team's name from the shell's own read, and a placeholder while that loads, never the slug a screen falls back to (`research.md` W-30, the smaller points of that audit).
  const teamName = data.workspace.data?.name;
  const teamCrumbs = useMemo(() => topBar.crumbs.map((crumb) => (crumb.to === `/${ws}` ? { ...crumb, label: teamName ?? crumb.label, loading: teamName === undefined } : crumb)), [topBar.crumbs, ws, teamName]);
  // Back, on a page under one of its mode's destinations (2026-10-07): to the page the person came from, else to the page's logical parent.
  const origin = useOrigin();
  const nested = isNestedPage(MODES, ws, location.pathname);
  const back = useMemo<BackTarget | null>(() => {
    if (!nested) return null;
    if (origin !== null) return { to: origin.href, label: origin.title, origin };
    const parent = parentOf({ crumbs: teamCrumbs, ...(topBar.parent === undefined ? {} : { parent: topBar.parent }) }, location.pathname);
    return parent?.to === undefined ? null : { to: parent.to, label: parent.loading === true ? null : parent.label, origin: null };
  }, [nested, origin, teamCrumbs, topBar.parent, location.pathname]);
  const me = data.me.data;
  const canCreate = canCreateProject(me, ws, data.workspace.data?.members_can_create_projects);
  const closeDrawer = useCallback(() => {
    ui.setSidebarDrawerOpen(false);
  }, [ui]);
  const switcher = me !== undefined ? <WorkspaceSwitcher runtime={runtime} ws={ws} me={me} workspaces={data.workspaces} current={data.workspace.data} onNavigate={closeDrawer} /> : <SwitcherPlaceholder />;
  const contextValue = useMemo<WorkspaceContextValue>(() => ({ ws, workspace: data.workspace.data }), [ws, data.workspace.data]);

  if (data.noAccess) {
    return (
      <FeaturesProvider value={features}>
        <div className={`b-shell ${frameGroundClassName}`} data-chrome>
          <div className={`b-shell__surface ${frameSheetClassName}`} style={{ gridColumn: "1 / -1" }}>
            <main ref={main} id="main" className="b-shell__main" tabIndex={-1}>
              <SheetLogo />
              {suspended ? (
                <TeamSuspended name={data.me.data?.memberships.find((membership) => membership.slug === ws)?.name ?? ws} switcher={switcher} />
              ) : former.isPending ? (
                <NoAccessSkeleton />
              ) : former.isSuccess ? (
                <TeamMoved former={former.data} />
              ) : (
                <NoAccess workspace={ws} problem={problemOf(data.workspace.error)} switcher={switcher} />
              )}
            </main>
          </div>
        </div>
      </FeaturesProvider>
    );
  }

  return (
    <FeaturesProvider value={features}>
      <WorkspaceContext.Provider value={contextValue}>
        <RecordReferenceProvider component={RecordReference}>
        <RecentColorsProvider value={recentColours}>
        <TopBarProvider value={topBar}>
          <AppShell
            rail={<Rail modes={modes} active={activeMode} homeRoute={`/${ws}/${HOME_MODE.landing}`} onSelectMode={selectMode} {...(me === undefined ? {} : { account: <RailAccount me={me} /> })} {...(assistantAvailable ? { assistant: <AssistantTile open={assistantPanel.open} onToggle={toggleAssistant} /> } : {})} help={<HelpMenu ws={ws} onShortcuts={() => { ui.setShortcutsOpen(true); }} />} />}
            sidebar={
              <Sidebar treeKey={(activeMode ?? HOME_MODE).id} switcher={switcher} collapsed={sidebarCollapsed} onCollapsedChange={onSidebarCollapsedChange}>
                <ModeSidebar ws={ws} unread={unread} onNavigate={closeDrawer} />
              </Sidebar>
            }
            topBar={<TopBar ws={ws} back={back} crumbs={teamCrumbs} actions={topBar.actions} unread={unread} create={<QuickCreateButton ws={ws} />} onOpenPalette={() => { ui.setPaletteOpen(true); }} onToggleDrawer={ui.toggleSidebarDrawer} />}
            banner={
              offline ? (
                <OfflineBanner
                  queuedWrites={queued}
                  retrying={retrying}
                  onRetry={() => {
                    setRetrying(true);
                    void runtime.offline.probe().finally(() => {
                      setRetrying(false);
                    });
                  }}
                />
              ) : connection.kind === "reconnecting" || connection.kind === "polling" || connection.kind === "limited" || connection.kind === "stopped" ? (
                <ConnectionBanner state={connection} />
              ) : isUnverified(me) && !ui.verifyBannerDismissed ? (
                <VerifyEmailBanner runtime={runtime} me={me} onDismiss={ui.dismissVerifyBanner} />
              ) : undefined
            }
            peek={<PeekSlot ws={ws} narrow={fold.narrow} />}
            dock={
              assistantSettings.isSuccess && assistantOpen ? (
                <Suspense fallback={<DockFallback narrow={fold.narrow} />}>
                  <AssistantDock ws={ws} settings={assistantSettings.data} instanceOrigin={window.location.origin} narrow={fold.narrow} />
                </Suspense>
              ) : undefined
            }
            sidebarWidth={sidebarWidth}
            onSidebarWidthChange={onSidebarWidthChange}
            sidebarCollapsed={sidebarCollapsed || fold.foldSidebar}
            drawerOpen={ui.sidebarDrawerOpen}
            onDrawerOpenChange={ui.setSidebarDrawerOpen}
          >
            {data.workspace.isError ? (
              <div className="b-page">
                <ErrorState form="full-page" problem={problemOf(data.workspace.error)} onRetry={() => void data.workspace.refetch()} />
              </div>
            ) : (
              <Outlet />
            )}
          </AppShell>
          {ui.paletteMounted && (
            <Suspense fallback={null}>
              <CommandPalette runtime={runtime} ws={ws} features={features} projects={data.projects} canCreateProject={canCreate} instanceAdmin={me?.is_instance_admin === true} workspaceAdmin={isWorkspaceAdmin(me, ws)} workspaceMember={isWorkspaceMember(me, ws)} grants={grants} />
            </Suspense>
          )}
          {ui.newProjectOpen && (
            <Suspense fallback={<DialogLoading title={t("project.new")} description={t("project.newBody")} size="sm" fields={2} onClose={() => { ui.setNewProjectOpen(false); }} />}>
              <NewProjectDialog ws={ws} />
            </Suspense>
          )}
          {ui.newItem.open && (
            <Suspense fallback={<DialogLoading title={t("item.new.title")} size="md" fields={1} editor onClose={() => { ui.setNewItemOpen(false, null); }} />}>
              <NewItemDialog ws={ws} />
            </Suspense>
          )}
          {ui.logInteraction.open && (
            <Suspense fallback={<LogInteractionLoading />}>
              <LogInteractionDialog ws={ws} />
            </Suspense>
          )}
          {ui.newClientOpen && (
            <Suspense fallback={<DialogLoading title={t("shell.create.client")} size="md" fields={3} onClose={() => { ui.setNewClientOpen(false); }} />}>
              <NewClientDialog ws={ws} />
            </Suspense>
          )}
          {ui.moveRequest !== null && (
            <Suspense
              fallback={
                <DialogLoading
                  title={ui.moveRequest.subjects.length === 1 && ui.moveRequest.subjects[0] !== undefined ? t("item.move.title", { title: ui.moveRequest.subjects[0].title }) : t("item.move.titleMany", { count: ui.moveRequest.subjects.length })}
                  description={t("item.move.body")}
                  size="md"
                  fields={1}
                  onClose={() => { ui.setMoveRequest(null); }}
                />
              }
            >
              <MoveDialog ws={ws} />
            </Suspense>
          )}
          {closing !== null && (
            <Suspense fallback={<DialogLoading title={t("item.closure.title", { count: closing.items.length })} size="sm" fields={2} onClose={() => { settleClosing(null); }} />}>
              <CloseReasonDialog />
            </Suspense>
          )}
          {reminding !== null && (
            <Suspense fallback={<DialogLoading title={t("item.remind.title", { title: reminding.title })} size="sm" fields={1} onClose={closeRemind} />}>
              <RemindDialog ws={ws} />
            </Suspense>
          )}
          {ui.shortcutsOpen && (
            <Suspense fallback={<DialogLoading title={t("shortcuts.title")} description={t("shortcuts.body")} size="lg" fields={6} onClose={() => { ui.setShortcutsOpen(false); }} />}>
              <ShellShortcutReference open onOpenChange={ui.setShortcutsOpen} />
            </Suspense>
          )}
          <RestoreNotice />
        </TopBarProvider>
        </RecentColorsProvider>
        </RecordReferenceProvider>
      </WorkspaceContext.Provider>
    </FeaturesProvider>
  );
}

